import { ReactNode, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { CalendarTodayOutlined, DescriptionOutlined, MonetizationOnOutlined } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { Button, ConsentRow, PageHeader } from "@components/ui_v2";
import { formatCurrencyWhole, formatPercent } from "@helpers/funciones";
import { EJEMPLO_DIAS, getOfertaFluxValues } from "@helpers/factoraje.helpers";
import { ALTO_SKELETON_EXPEDIENTE } from "@helpers/constantes";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useLazyGetConditionsByIdQuery } from "@api/applications";
import { ApiError, LoanCondiciones } from "@interfaces/response";

const ICON_SX = { fontSize: 15 };

interface FirmaSectionProps {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}

/** `FirmaSection` del diseño: card con un ícono en cuadro de 28px y el título en el color de marca. */
const FirmaSection = ({ icon, title, children }: FirmaSectionProps) => (
  <Paper variant="lendia_paper" sx={{ p: "20px 22px", mb: "14px" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "14px" }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "7px",
          bgcolor: "tokens.infoBg",
          border: "1px solid",
          borderColor: "tokens.infoBorder",
          color: "primary.main",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 13, color: "primary.main", letterSpacing: "0.01em" }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

/** `.firma-stats`: tres columnas separadas por una línea vertical. */
const FirmaStats = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
      gap: "18px",
      alignItems: "start",
      "& > * + *": {
        borderLeft: { xs: "none", sm: "1px solid" },
        borderColor: "tokens.borderStrong",
        pl: { xs: 0, sm: "18px" },
      },
    }}
  >
    {children}
  </Box>
);

interface FirmaStatProps {
  label: string;
  value: string;
  note?: string;
  highlight?: boolean;
}

const FirmaStat = ({ label, value, note, highlight }: FirmaStatProps) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        fontSize: 11.5,
        color: "tokens.textTertiary",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        mb: "6px",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontWeight: 600, letterSpacing: "-0.015em", fontSize: highlight ? 22 : 18, color: "primary.dark" }}
    >
      {value}
    </Typography>
    {note && <Typography sx={{ fontSize: 11.5, color: "tokens.textTertiary", mt: "4px" }}>{note}</Typography>}
  </Box>
);

/** Carátula de la línea de factoraje antes de firmar (`S_FirmaCondiciones` del diseño, modo Flux). */
export const BienvenidaFirmaFactoraje = () => {
  const dispatch = useAppDispatch();
  const application = useSelector((state: RootState) => state.register.application);
  const [getConditions] = useLazyGetConditionsByIdQuery();
  const [conditions, setConditions] = useState<LoanCondiciones | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [acepta, setAcepta] = useState(false);

  const loadConditions = useCallback(async () => {
    if (!application?.id) {
      setIsLoading(false);
      return;
    }
    try {
      const data = await getConditions(application.id).unwrap();
      setConditions(data[0] ?? null);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "BienvenidaFirmaFactoraje" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  }, [application?.id, getConditions, dispatch]);

  useEffect(() => {
    loadConditions();
  }, [loadConditions]);

  const { lineaPreaprobada, aforo, tasa, comision, plazoMax, facturaEjemplo, anticipo, costoEjemplo } =
    getOfertaFluxValues(conditions);

  return (
    <Box>
      <PageHeader
        eyebrow="Formalización · Condiciones de la línea"
        title="Revisa las condiciones de tu línea de factoraje"
        subtitle="Antes de firmar, confirma las condiciones definitivas calculadas con tu expediente autorizado."
      />

      {isLoading ? (
        <Skeleton variant="rectangular" height={ALTO_SKELETON_EXPEDIENTE} sx={{ borderRadius: 2 }} />
      ) : (
        <>
          <FirmaSection icon={<MonetizationOnOutlined sx={ICON_SX} />} title="Condiciones de la línea">
            <FirmaStats>
              <FirmaStat label="Monto de la línea" value={`${formatCurrencyWhole(lineaPreaprobada)} MXN`} highlight />
              <FirmaStat label="Tipo de línea" value="Revolvente" note="Se libera conforme te pagan tus clientes" />
              <FirmaStat label="Plazo máximo de factura" value={`${plazoMax} días`} />
            </FirmaStats>
          </FirmaSection>

          <FirmaSection icon={<DescriptionOutlined sx={ICON_SX} />} title="Costo por operación">
            <FirmaStats>
              <FirmaStat label="Aforo" value={formatPercent(aforo)} note="Anticipo sobre el valor de cada factura" />
              <FirmaStat label="Tasa anual" value={formatPercent(tasa)} note="Se cobra por los días de la factura" />
              <FirmaStat label="Comisión por operación" value={formatPercent(comision)} />
            </FirmaStats>
          </FirmaSection>

          <FirmaSection
            icon={<CalendarTodayOutlined sx={ICON_SX} />}
            title={`Ejemplo con una factura de ${formatCurrencyWhole(facturaEjemplo)} a ${EJEMPLO_DIAS} días`}
          >
            <FirmaStats>
              <FirmaStat label="Anticipo" value={formatCurrencyWhole(anticipo)} />
              <FirmaStat label="Costo total" value={formatCurrencyWhole(costoEjemplo)} />
              <FirmaStat label="Recibes hoy" value={formatCurrencyWhole(anticipo - costoEjemplo)} highlight />
            </FirmaStats>
          </FirmaSection>

          <ConsentRow
            checked={acepta}
            onChange={setAcepta}
            label={
              <>
                Declaro, bajo protesta de decir verdad, comprender las{" "}
                <strong>condiciones de mi línea de factoraje</strong> y el <strong>costo de cada operación</strong> aquí
                descritos.
              </>
            }
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              mt: 4,
              pt: "20px",
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button tone="outline">Solicitar cambio</Button>
            <Button tone="primary" withArrow disabled={!acepta}>
              Continuar a firma
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};
